import { useEffect, useState } from 'react'

import { useInView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'

import IconMusic from '@/assets/icons/icon-music.svg'
import { Page } from '@/components/layout/page/page'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { TikTokMediaGridSkeleton } from '@/modules/tiktok-research/skeletons/TikTokMediaGridSkeleton.tsx'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import { getSoundById } from '../../service'
import { TikTokVideosFilters } from '../../videos/ui/filters/TikTokVideosFilters.tsx'
import type { TikTokVideosFiltersForm } from '../../videos/ui/filters/validation.ts'
import { defaultValues } from '../../videos/ui/filters/validation.ts'
import { VideoGrid } from '../../videos/ui/video-grid/VideoGrid.tsx'

interface Props {
  soundId: string
  backPage: {
    name: string
    url: string
  }
}

export const TikTokSoundsTrendingVideosView = ({
  soundId,
  backPage,
}: Props) => {
  const { ref, inView } = useInView()
  const [filters, setFilters] = useState<TikTokVideosFiltersForm>(defaultValues)
  const { data, fetchNextPage, isFetchingNextPage, hasNextPage, isFetching } =
    useInfiniteQuery(
      ['sound', soundId, JSON.stringify(filters)],
      ({ pageParam = 1, signal }) =>
        getSoundById(soundId, filters, pageParam, signal),
      {
        getNextPageParam: (page, pages) =>
          page.posts.last ? undefined : pages.length + 1,
        refetchOnMount: true,
      },
    )

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  const posts = data?.pages
    .flatMap((page) => page.posts.content)
    .filter(isTruthyAndNotEmpty)

  const { title, author } = data?.pages[0].sound ?? {}

  return (
    <Page
      backToPage={backPage}
      title={
        <div className="mt-2 flex items-center gap-3">
          <IconMusic className="shrink-0" />
          {isFetching && !isFetchingNextPage ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            <div className="text-xl">{title}</div>
          )}
        </div>
      }
      description={author}
      className="mb-8 lg:gap-6"
      headerOptions={
        <TikTokVideosFilters
          value={filters}
          onChange={setFilters}
        />
      }
    >
      {isFetching && !isFetchingNextPage ? (
        <TikTokMediaGridSkeleton />
      ) : (
        <VideoGrid
          videos={posts ?? []}
          lastElementRef={ref}
        />
      )}
    </Page>
  )
}
